import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import echo from '../echo';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import Footerr from './footers/Footer';
import Light from './headers/light';
import './clientChat.css';
import { useTranslation } from 'react-i18next';

const ClientChat = () => {
  const { t } = useTranslation();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [error, setError] = useState(null);
  const [loadingSession, setLoadingSession] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Generate random ID and name for guest users
  const generateGuestId = () => {
    const guestId = localStorage.getItem('guest_id');
    if (guestId) return guestId;
    // Generate a large random number to avoid conflicts with real user IDs
    const newGuestId = Math.floor(900000000 + Math.random() * 100000000);
    localStorage.setItem('guest_id', newGuestId.toString());
    return newGuestId.toString();
  };

  const generateGuestName = () => {
    const guestName = localStorage.getItem('guest_name');
    if (guestName) return guestName;
    const newGuestName = 'Guest_' + Math.random().toString(36).substr(2, 6).toUpperCase();
    localStorage.setItem('guest_name', newGuestName);
    return newGuestName;
  };

  const storedUserId = localStorage.getItem('id') || generateGuestId();
  const storedClientName = localStorage.getItem('user_name') || generateGuestName();
  const api = useMemo(() => axios.create({ baseURL: process.env.REACT_APP_API_URL }), []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!storedUserId || !storedClientName) return;
    const createOrFetchSession = async () => {
      setLoadingSession(true);
      try {
        const res = await api.post('/client/session', {
          user_id: parseInt(storedUserId, 10),
          client_name: storedClientName.toString(),
        });
        setSession(res.data);
        setError(null);
      } catch (err) {
        console.error('Error creating/fetching session:', err);
        console.error('Error response:', err.response?.data);
        console.error('Sent data:', { user_id: parseInt(storedUserId, 10), client_name: storedClientName });
        setError(t('clientChat.failedStartSession') + ' ' + (err.response?.data?.message || err.message || ''));
      } finally {
        setLoadingSession(false);
      }
    };
    createOrFetchSession();
  }, [storedUserId, storedClientName, api, t]);

 // Fetch messages when session changes
 useEffect(() => {
  if (!session) return;
  const fetchMessages = async () => {
    setLoadingMessages(true);
    try {
      const res = await api.get(`/client/messages/${session.id}`);
      setMessages(res.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching client messages:', err);
      setError(t('clientChat.failedLoadMessages') + ' ' + (err.message || ''));
    } finally {
      setLoadingMessages(false);
    }
  };
  fetchMessages();
 }, [session, api, t]);


// Real-time subscription with diagnostics & fallbacks
useEffect(() => {
  if (!session) return;
  const baseChannel = 'chat';
  const possibleChannels = [
    baseChannel, // public channel
    `private-${baseChannel}`, // automatic Laravel private naming when using echo.channel may require echo.private
    `${baseChannel}.${session.id}`, // session-scoped channel
    `private-${baseChannel}.${session.id}`,
  ];

  let activeChannelRef = { name: null, type: null };
  let subscribed = false;

  const attachListeners = (channelObj, channelName, type) => {
    activeChannelRef = { name: channelName, type };
    channelObj.listen('PrivateMessageSent', (e) => {
      console.log('[Echo] Event PrivateMessageSent received on', channelName, 'payload:', e);
      const incoming = e.message ? {
        id: e.id || Date.now(),
        sender_type: e.sender_type || e.sender || 'client',
        message: e.message || e.text || JSON.stringify(e),
        created_at: e.created_at || new Date().toISOString(),
      } : e; // fallback if structure matches what we expect already
      setMessages(prev => [...prev, incoming]);
    });
  };

  console.log('[Echo] Attempting subscription candidates:', possibleChannels);

  try {
    for (const candidate of possibleChannels) {
      if (subscribed) break;
      try {
        // Decide whether to use private channel helper
        let channelObj;
        if (candidate.startsWith('private-')) {
          const stripped = candidate.replace(/^private-/, '');
          channelObj = echo.private(stripped);
          attachListeners(channelObj, candidate, 'private');
        } else if (candidate.startsWith(baseChannel) && candidate !== baseChannel) {
          // Likely presence or private pattern; try private first then public
          try {
            channelObj = echo.private(candidate);
            attachListeners(channelObj, candidate, 'private');
          } catch (e) {
            channelObj = echo.channel(candidate);
            attachListeners(channelObj, candidate, 'public');
          }
        } else {
          channelObj = echo.channel(candidate);
          attachListeners(channelObj, candidate, 'public');
        }
        subscribed = true;
        console.log('[Echo] Subscribed to', activeChannelRef);
      } catch (innerErr) {
        console.warn('[Echo] Subscription failed for', candidate, innerErr);
      }
    }
  } catch (err) {
    console.error('[Echo] Unexpected subscription error:', err);
    setError('Real-time subscription failed. ' + (err.message || ''));
  }

  // Connection diagnostics
  if (echo?.connector?.pusher?.connection) {
    const connection = echo.connector.pusher.connection;
    connection.bind('state_change', (states) => {
      console.log('[Echo] Connection state_change', states.previous, '->', states.current);
    });
    connection.bind('error', (err) => {
      console.error('[Echo] Connection error', err);
      setError('Real-time connection error. Verify Pusher credentials & backend broadcasting config.');
    });
  }

  return () => {
    if (activeChannelRef.name) {
      console.log('[Echo] Leaving channel', activeChannelRef);
      try { echo.leave(activeChannelRef.name); } catch (_) {}
    }
  };
}, [session]);

// Polling fallback every 10s if realtime not established
useEffect(() => {
  if (!session) return;
  const interval = setInterval(async () => {
    try {
      const res = await api.get(`/client/messages/${session.id}`);
      // Merge new messages only
      setMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const incoming = res.data.filter(m => !existingIds.has(m.id));
        return incoming.length ? [...prev, ...incoming] : prev;
      });
    } catch (e) {
      // Silent polling error
    }
  }, 10000); // 10 seconds
  return () => clearInterval(interval);
}, [session, api]);

 const sendMessage = () => {
  if (!newMsg.trim() || !session || isSending) return;

  setIsSending(true);
  const messageToSend = newMsg;
  setNewMsg(''); // Clear input immediately
  
  api.post('/client/send', { session_id: session.id, message: messageToSend })
    .then(() => {
      setError(null);
      // Message will be added via real-time listener (Echo)
    })
    .catch(err => {
      console.error('Error sending message:', err);
      setError('Failed to send message. ' + (err.message || ''));
      // Restore message on error so user can retry
      setNewMsg(messageToSend);
    })
    .finally(() => {
      setIsSending(false);
    });
};


  const isClientMessage = (m) => m.sender_type === 'client' || m.sender_type === 'Client';
  console.log("storedUserId",storedUserId);
  console.log('storedClientName',storedClientName);

  return (
    <AnimationRevealPage>
      <Light />
      <div className="client-chat-page">
        <div className="client-chat-container">
          <section className="client-chat-main">
            {error && (
              <div className="client-chat-error-banner">
                {error} {loadingSession || loadingMessages ? t('clientChat.retrying') : ''}
              </div>
            )}
            <header className="client-chat-main-header">
              <div className="client-chat-main-title">{t('clientChat.support')}</div>
              <div className="client-chat-main-subtitle">
                {t('clientChat.replyInMinutes')}
              </div>
            </header>

            <div className="client-chat-messages">
              {loadingMessages && (
                <div className="client-chat-loading">{t('clientChat.loadingMessages')}</div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={
                    'client-chat-message-row ' + (isClientMessage(m) ? 'me' : 'them')
                  }
                >
                  <div
                    className={
                      'client-chat-message-bubble ' +
                      (isClientMessage(m) ? 'me' : 'them')
                    }
                  >
                    {m.message}
                    {m.created_at && (
                      <div className="client-chat-message-meta">
                        {new Date(m.created_at).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <div className="client-chat-input-bar">
              <input
                className="client-chat-input"
                value={newMsg}
                onChange={(e) => setNewMsg(e.target.value)}
                placeholder={t('clientChat.typePlaceholder')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') sendMessage();
                }}
                disabled={isSending}
              />
              <button 
                className="client-chat-send-btn" 
                onClick={sendMessage}
                disabled={isSending}
              >
                {isSending ? t('clientChat.sending') : '➤'}
              </button>
            </div>
          </section>
        </div>
      </div>
      <Footerr />
    </AnimationRevealPage>
  );
};

export default ClientChat;
